import { DeviceInformationTable } from '@/pages/new-rma/@x/rma-details';
import _ from 'lodash';
import { forwardRef } from 'react';
import { t } from 'ttag';
import LabelInformation from './labelInformation';
import { useTicketInbound } from './useTicketInbound';
import type { TicketInboundProps } from './ticketInbound.types';

/**
 * Inbound view of an RMA ticket, showing the returned devices and their shipment tracking. Reads the ticket breakdown from useTicketInbound and renders the inbound DeviceInformationTable, notification email badges and clinic notes, a paired shipping and returning address table, and one LabelInformation per label whose refresh forwards to handleRefreshTracking tagged as Inbound, falling back to a no shipping label created yet message.
 *
 * @returns Inbound RMA ticket content element.
 */
const TicketInbound = forwardRef(
  ({
    hasRenewalStock,
    listStatesUS,
    listStatesCA,
    listCountries,
    data,
    handleRefreshTracking,
  }: TicketInboundProps) => {
    const { state, rmaDevicesArray, notificationEmail, clinicNotes, shippingInfo, labels } =
      useTicketInbound({ data });

    /**
     * Renders the notification email recipients of an inbound RMA ticket as badges. Maps each address in the list to a badge item inside a badge list container.
     *
     * @param notificationEmail - Email addresses notified about the RMA ticket.
     * @returns Badge list element holding one badge per email address.
     */
    const renderNotificationEmail = (notificationEmail: any) => (
      <div className="badge-email-list">
        {_.map(notificationEmail, (email) => (
          <div className="badge-email-item">{email}</div>
        ))}
      </div>
    );

    return (
      <div className="rma-detail-content">
        <DeviceInformationTable
          isInbound
          rmaDevicesArray={rmaDevicesArray}
          hasRenewalStock={hasRenewalStock}
        />

        <div className="other-information">
          <div className="input-title">{t`Other information`}</div>
          <div className="other-information-content">
            <div className="other-information-row">
              <span className="title">{`${t`Notification email`}:`}</span>
              <span className="description">{renderNotificationEmail(notificationEmail)}</span>
            </div>
            <div className="other-information-row">
              <span className="title">{`${t`Notes`}:`}</span>
              <span className="description">{clinicNotes}</span>
            </div>
          </div>
        </div>

        <div className="shipping-information-table">
          <div className="input-title">{t`Shipping information`}</div>
          <div className="shipping-information-table-wrapper">
            <div className="table-title">
              <div className="shipping-title">{t`Shipping address`}</div>
              <div className="returning-title">{t`Returning address`}</div>
            </div>
            {_.map(shippingInfo, (x) => (
              <div className="table-row">
                <div className="shipping-row">
                  <div className="title">{x.title}</div>
                  <div className="description">{x.shippingContent}</div>
                </div>
                <div className="returning-row">
                  <div className="title">{x.title}</div>
                  <div className="description">{x.returningContent}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div id="inboundTrackingInformation" className="tracking-information">
          <div className="input-title">{t`Tracking information`}</div>
          {labels?.length ? (
            _.map(labels, (item, index) => (
              <div className="tracking-information-item">
                <LabelInformation
                  filePDF={state.filePDF}
                  index={index}
                  rate={item.rate}
                  stateUS={listStatesUS}
                  stateCA={listStatesCA}
                  country={listCountries}
                  shipmentDate={item.shipmentDate}
                  trackingHistory={item.trackingHistory}
                  transaction={item.transaction}
                  deviceLabel={_.map(item.shipmentDevices, (device) => device.deviceId)}
                  id={data.id}
                  rmaId={data.rmaId}
                  handleRefreshTracking={({ transactionId, ticketId, index, callback }) => {
                    handleRefreshTracking({
                      type: 'Inbound',
                      transactionId,
                      ticketId,
                      index,
                      callback,
                    });
                  }}
                />
              </div>
            ))
          ) : (
            <div className="no-shipping-label">{t`No shipping label created yet`}</div>
          )}
        </div>
      </div>
    );
  },
);

export default TicketInbound;
